import type { AnyAction, Middleware, MiddlewareAPI } from "redux";
import { isActionOf } from "typesafe-actions";

import { resetLeadGeneratorThreadIfPresent } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/store";
import { companyAnalysisQueryClient } from "app/pages/sales-intelligence/modules/company-analysis/queries/client";
import { industryTaxonomyOptedIn } from "app/pages/sales-intelligence/modules/industry/store/action-creators";
import { resetTabsPagination } from "app/pages/sales-intelligence/modules/leads-search/store/action-creators";
import { savedSearchFiltersQueryKeys } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/query/queryKeyFactory";
import { updateLeadsFilterValue } from "app/pages/sales-intelligence/modules/leads-search-filters/store/action-creators";
import { listsQueryKeys } from "app/pages/sales-intelligence/modules/lists/sub-modules/my-list/services/queries/queryKeyFactory";
import { teamsQueryKeys } from "app/pages/sales-intelligence/modules/teams/queries/queryKeyFactory";
import { errorOperationMap } from "app/pages/sales-intelligence/utils/errors/context";
import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import { errorCategoryMap, SiError } from "app/pages/sales-intelligence/utils/errors/types";
import queryClient from "single-spa/react-app/query-client";
import type { RootState, ThunkDispatchCommon } from "single-spa/store/types";

/**
 * Reacts to a successful industry-taxonomy v2 opt-in. Kept as its own middleware rather than a case in the
 * shared actions middleware so this one concern owns its module dependencies instead of swelling a central
 * switch. The industry module only dispatches {@link industryTaxonomyOptedIn}; the cross-module fan-out lives
 * here.
 *
 * Opting in migrates every industry-classified asset to v2 on the backend, but no query key encodes the
 * taxonomy version, so these caches would keep serving stale v1 data — we invalidate them by base-key prefix.
 * We also strip any applied legacy industry filter: once opted in its card no longer renders, so a lingering
 * value would silently filter results with no way to see or remove it. `["industry"]` is left untouched — its
 * O(n) taxonomy tree is isolated so enrolling cannot evict it.
 *
 * We also reset the leadgen AI agent thread when one exists: its conversation is anchored to the pre-migration
 * (v1) classification, so we discard it and start a fresh thread. No thread ⇒ no-op (the next agent open makes
 * a fresh, already-post-migration one).
 */
const industryOptInMiddleware: Middleware<Record<PropertyKey, unknown>, RootState, ThunkDispatchCommon> =
    (store: MiddlewareAPI<ThunkDispatchCommon, RootState>) =>
    (next: (action: AnyAction) => AnyAction) =>
    (action: AnyAction): AnyAction => {
        const result = next(action);

        if (!isActionOf(industryTaxonomyOptedIn, action)) {
            return result;
        }

        try {
            store.dispatch(updateLeadsFilterValue({ leadsFilters: { industryFilter: undefined } }));
            store.dispatch(resetTabsPagination());

            void companyAnalysisQueryClient.invalidateCompanyCardQueries();
            void companyAnalysisQueryClient.invalidateCompetitorsQueries();
            void queryClient.invalidateQueries({ queryKey: teamsQueryKeys.all() });
            void queryClient.invalidateQueries({ queryKey: listsQueryKeys.allSavedSearches() });
            void queryClient.invalidateQueries({ queryKey: savedSearchFiltersQueryKeys.getById() });

            void store.dispatch(resetLeadGeneratorThreadIfPresent()).catch((error) =>
                trackError(
                    new SiError(
                        "Failed to reset leadgen agent thread after industry taxonomy opt-in",
                        {
                            category: errorCategoryMap.service,
                            component: "industryOptInMiddleware",
                            operation: errorOperationMap.resetThread,
                        },
                        { cause: error },
                    ),
                ),
            );
        } catch (error) {
            trackError(
                new SiError(
                    "Failed to refresh caches after industry taxonomy opt-in",
                    {
                        category: errorCategoryMap.service,
                        component: "industryOptInMiddleware",
                        operation: errorOperationMap.invalidateCaches,
                    },
                    { cause: error },
                ),
            );
        }

        return result;
    };

export default industryOptInMiddleware;
